import { ReactElement, ReactNode, useCallback, useEffect, useState, useRef } from 'react';
import {
  AuthError,
  AuthSession,
  AuthUser,
  fetchAuthSession,
  getCurrentUser,
  signInWithRedirect,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { USER_POOL_IDP } from './AuthenticationWrapper';
import { logger } from 'src/logger';
import { metricsPublisher } from 'src/metrics';
import { DIMENSION_KEYS, METRIC_NAME } from 'src/types';
import { AuthenticationContext } from 'src/contexts/AuthenticationContext';
import { AuthConstants } from 'src/constants';
import { getGivenName, getUserAlias } from './AuthenticationUtils';
import { useToast } from 'src/contexts/ToastContext';
import { getErrorMessage } from 'src/utils/ErrorUtils';
import { sessionContext } from 'src/utils/SessionContext';
import { authenticatedLogger } from 'src/authenticatedLogger';
import { LoadingOverlay } from '../general/LoadingOverlay';

const STATUS_MESSAGES = {
  authenticating: 'Authenticating...',
  error: 'Authentication failed',
  retrying: 'Authentication failed. Retrying...',
  sessionExpired: 'Your session has expired. Redirecting to sign in...',
};

const MAX_RETRIES = 2; // Will try 3 times total (initial + 2 retries)
const RETRY_DELAY = 2000; // 2 seconds between retries
const USER_ALREADY_AUTHENTICATED_EXCEPTION = 'UserAlreadyAuthenticatedException';

/**
 * Stores the current pathname in sessionStorage for post-authentication redirection.
 * This ensures the user is redirected back to the page they were on after signing in.
 */
const setPostAuthRedirect = () => {
  const pathname = window.location.pathname;
  window.sessionStorage.setItem(AuthConstants.POST_AUTH_REDIRECT, pathname);
};

/**
 * Controls authentication flow and provides auth context to child components.
 *
 * Responsibilities:
 * - Manages authentication state
 * - Listens for auth events
 * - Handles redirect to federated sign-in
 * - Provides auth context to child components
 *
 * @component
 * @param {Object} props - Component props
 * @param {ReactNode} [props.children] - Child components to render when authenticated
 * @returns {ReactElement} Renders children wrapped in AuthenticationContext.Provider when authenticated,
 *                        or loading/redirect states when not authenticated
 *
 * @example
 * <AuthenticationController>
 *   <ProtectedComponent />
 * </AuthenticationController>
 */
const AuthenticationController = (props: { children?: ReactNode }): ReactElement => {
  const [user, setUser] = useState<AuthUser | undefined>();
  const [authSession, setAuthSession] = useState<AuthSession | undefined>();
  const [loading, setLoading] = useState(true);
  const isAuthenticating = useRef(false);
  const retryCount = useRef(0);
  const retryTimeout = useRef<NodeJS.Timeout>();
  const isMounted = useRef(false);
  const toast = useToast();

  const redirectToAmazonFederateSignIn = useCallback(async () => {
    setPostAuthRedirect();
    logger.debug('Redirecting to Amazon Federate Sign-In...', {
      pathname: window.location.pathname,
    });
    try {
      await signInWithRedirect({
        provider: {
          custom: USER_POOL_IDP,
        },
      });
    } catch (error) {
      if (error instanceof AuthError && error.name === USER_ALREADY_AUTHENTICATED_EXCEPTION) {
        logger.debug('User already authenticated, skipping sign-in redirect...');
        return;
      }
      throw error;
    }
  }, []);

  const scheduleRetry = useCallback(() => {
    if (retryCount.current >= MAX_RETRIES) {
      logger.warn(
        `Authentication failed after ${MAX_RETRIES + 1} attempts, showing toast and redirecting...`,
        {
          retryCount: retryCount.current,
          maxRetries: MAX_RETRIES,
        },
      );
      toast.addToast({ description: STATUS_MESSAGES.sessionExpired, variant: 'danger' });
      redirectToAmazonFederateSignIn();
      return;
    }

    // Show retrying toast
    logger.debug(
      `Scheduling retry attempt ${retryCount.current + 1}/${MAX_RETRIES + 1} in ${RETRY_DELAY}ms`,
      {
        currentRetry: retryCount.current + 1,
        maxRetries: MAX_RETRIES + 1,
        delayMs: RETRY_DELAY,
      },
    );
    toast.addToast({ description: STATUS_MESSAGES.retrying, variant: 'warning' });

    retryTimeout.current = setTimeout(async () => {
      retryCount.current++;
      await getAuthState();
    }, RETRY_DELAY);
  }, []);

  useEffect(() => {
    /**
     * Listens for authentication events from AWS Amplify Hub.
     * Updates the user and authentication session based on events.
     * Handles token refresh failures with retries.
     * @param {Object} payload - The payload of the authentication event.
     * @param {string} payload.event - The type of authentication event.
     * @param {string} payload.message - The message associated with the event.
     *
     * References:
     * - {@link https://docs.aws.amazon.com/amplify/latest/userguide/hub-events.html|Hub Overview}
     * - {@link https://aws.amplify.github.io/amplify-js/api/classes/hub.html|Hub API}
     * - {@link https://docs.aws.amazon.com/amplify/latest/userguide/auth-events.html|Auth Events}
     */
    const unsubscribe = Hub.listen('auth', ({ payload: { event, message } }) => {
      if (!isMounted.current) return;

      switch (event) {
        case 'signedIn':
          logger.debug('Sign-in event received, fetching user details...');
          getAuthState().then((sessionData) => {
            if (isMounted.current && sessionData?.user?.username && sessionData?.session) {
              const givenName = getGivenName(sessionData.session);
              logger.debug(`Showing welcome toast for ${givenName}`);
              toast.addToast({ description: `Welcome, ${givenName}!`, variant: 'success' });
            }
          });
          break;
        case 'signInWithRedirect':
        case 'tokenRefresh':
          logger.debug(`${event} event received, refreshing user details...`);
          getAuthState();
          break;
        case 'tokenRefresh_failure':
          logger.warn(`Token refresh failed: ${message}`, {
            event,
            error: new Error(message),
            retryCount: retryCount.current,
          });
          scheduleRetry();
          break;
        default:
          logger.debug(`Unhandled auth event: ${event}`, { message });
      }
    });

    logger.debug('Authentication controller mounted, initializing user session...');
    isMounted.current = true;
    getAuthState();

    return () => {
      logger.debug('Authentication controller unmounting, cleaning up...');
      isMounted.current = false;
      isAuthenticating.current = false;
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      unsubscribe();
    };
  }, [toast]);

  // Check authentication status and redirect if needed
  useEffect(() => {
    if (!loading && !user) {
      logger.debug('No authenticated user found, redirecting to sign-in...');
      redirectToAmazonFederateSignIn();
    }
  }, [loading, user]);

  async function getAuthState(): Promise<{ user: AuthUser; session: AuthSession } | undefined> {
    try {
      // If we're already authenticating, skip this request
      if (isAuthenticating.current) {
        logger.debug('Authentication already in progress, skipping duplicate request');
        return;
      }

      isAuthenticating.current = true;
      logger.debug('Starting authentication process...');

      try {
        if (!isMounted.current) return;
        const [currentUser, authSession] = await metricsPublisher.trackLatency(
          METRIC_NAME.AUTHENTICATION_LATENCY,
          () => Promise.all([getCurrentUser(), fetchAuthSession()]),
        );
        setUser(currentUser);
        setAuthSession(authSession);

        logger.debug('Authentication successful', {
          username: currentUser.username,
          userId: currentUser.userId,
          signInDetails: currentUser.signInDetails,
        });

        // Add user dimension and emit initial metrics after successful authentication
        metricsPublisher.addGlobalDimensions({ [DIMENSION_KEYS.USER_ID]: currentUser.userId });
        metricsPublisher.emitCount(METRIC_NAME.AUTHENTICATION_SUCCESS);

        sessionContext.update({
          userId: currentUser.userId,
          userAlias: getUserAlias(currentUser),
        });
        // Emit auth_complete regardless of logger init success — the emit will
        // no-op if logger isn't available, but we still want session state tracked.
        authenticatedLogger
          .getLogger()
          .catch(() => {})
          .then(() => {
            sessionContext.emit('auth_complete');
          });

        // Reset retry count on successful auth
        retryCount.current = 0;
        return { user: currentUser, session: authSession };
      } catch (error) {
        if (!isMounted.current) return;
        metricsPublisher.emitCount(METRIC_NAME.AUTHENTICATION_FAILURE);
        logger.warn('Authentication failed:', {
          error: getErrorMessage(error),
          retryCount: retryCount.current,
        });
        setUser(undefined);
        setAuthSession(undefined);
        return undefined;
      }
    } finally {
      logger.debug('Authentication process complete, resetting flags...', {
        isStillMounted: isMounted.current,
        hadError: !user,
      });
      isAuthenticating.current = false;
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }

  if (loading || !user) {
    return <LoadingOverlay text={STATUS_MESSAGES.authenticating} />;
  }

  return (
    <AuthenticationContext.Provider
      value={{
        user,
        authSession,
      }}
    >
      {props.children}
    </AuthenticationContext.Provider>
  );
};

export { AuthenticationController, STATUS_MESSAGES };
